/**
 * CheckInScheduleSettings Component
 * Configure check-in frequency and day for an OKR
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Settings, Clock, Check, AlertTriangle } from 'lucide-react';
import { Card, Button, LoadingSpinner } from '../common';
import { useUpdateCheckInSchedule } from '../../hooks';
import type { OKR } from '../../types/okr';
import { format } from 'date-fns';

interface CheckInScheduleSettingsProps {
  okr: OKR;
  canConfigure: boolean;
}

const frequencyOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'none', label: 'No Check-ins' }
];

const dayOfWeekOptions = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Statuses that allow check-ins (active OKRs)
const CHECKIN_ALLOWED_STATUSES = ['active', 'on_track', 'at_risk', 'off_track'];

export const CheckInScheduleSettings: React.FC<CheckInScheduleSettingsProps> = ({
  okr,
  canConfigure
}) => {
  const updateScheduleMutation = useUpdateCheckInSchedule();
  const [isEditing, setIsEditing] = useState(false);
  const [frequency, setFrequency] = useState<'weekly' | 'biweekly' | 'monthly' | 'none'>(
    (okr.checkInFrequency as 'weekly' | 'biweekly' | 'monthly' | 'none') || 'weekly'
  );
  const [day, setDay] = useState(okr.checkInDay ?? 5); // Default Friday

  // Check if OKR status allows check-ins
  const isCheckInAllowed = CHECKIN_ALLOWED_STATUSES.includes(okr.status);

  // Reset form when okr changes
  useEffect(() => {
    setFrequency((okr.checkInFrequency as 'weekly' | 'biweekly' | 'monthly' | 'none') || 'weekly');
    setDay(okr.checkInDay ?? 5);
  }, [okr.checkInFrequency, okr.checkInDay]);

  const handleSave = async () => {
    try {
      await updateScheduleMutation.mutateAsync({
        okrId: okr.id,
        data: {
          checkInFrequency: frequency,
          checkInDay: day
        }
      });
      setIsEditing(false);
    } catch (error) {
      // Error is handled by the mutation
    }
  };

  const handleCancel = () => {
    setFrequency((okr.checkInFrequency as 'weekly' | 'biweekly' | 'monthly' | 'none') || 'weekly');
    setDay(okr.checkInDay ?? 5);
    setIsEditing(false);
  };

  const formatNextDue = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Not scheduled';
    try {
      const date = new Date(dateStr);
      return format(date, "EEEE, MMM d, yyyy");
    } catch {
      return 'Not scheduled';
    }
  };

  const getFrequencyLabel = (freq: string | null | undefined) => {
    if (!freq || freq === 'none') return 'No scheduled check-ins';
    const option = frequencyOptions.find(f => f.value === freq);
    return option?.label || freq;
  };

  const getDayLabel = (dayNum: number | null | undefined, freq: string | null | undefined) => {
    if (dayNum === null || dayNum === undefined) return '';
    if (freq === 'monthly') {
      return `on the ${dayNum}${getOrdinalSuffix(dayNum)}`;
    }
    const option = dayOfWeekOptions.find(d => d.value === dayNum);
    return option ? `on ${option.label}` : '';
  };

  // Read-only view
  if (!isEditing) {
    return (
      <Card className="p-4">
        {/* Warning for draft OKRs */}
        {!isCheckInAllowed && (
          <div className="flex items-start gap-2 p-3 mb-3 bg-warning-50 border border-warning-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-warning-700">
              <p className="font-medium">Check-ins not available</p>
              <p>This OKR must be approved and active before check-ins will appear in your calendar and pending list.</p>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isCheckInAllowed ? 'bg-primary-50' : 'bg-gray-100'}`}>
              <Calendar className={`w-5 h-5 ${isCheckInAllowed ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Check-in Schedule</p>
              {okr.checkInFrequency && okr.checkInFrequency !== 'none' ? (
                <div className="text-xs text-gray-500 space-y-0.5">
                  <p>{getFrequencyLabel(okr.checkInFrequency)} {getDayLabel(okr.checkInDay, okr.checkInFrequency)}</p>
                  {isCheckInAllowed && (
                    <p className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Next due: {formatNextDue(okr.nextCheckInDue)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No scheduled check-ins</p>
              )}
            </div>
          </div>
          {canConfigure && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Settings className="w-4 h-4 mr-1" />
              Configure
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // Edit view
  return (
    <Card className="p-4">
      <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
        <Calendar className="w-5 h-5 text-primary-600" />
        Configure Check-in Schedule
      </h4>

      <div className="space-y-4">
        {/* Frequency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Frequency
          </label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as 'weekly' | 'biweekly' | 'monthly' | 'none')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {frequencyOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Day Selection */}
        {frequency !== 'none' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {frequency === 'monthly' ? 'Day of Month' : 'Day of Week'}
            </label>
            {frequency === 'monthly' ? (
              <select
                value={day}
                onChange={(e) => setDay(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>
                    {d}{getOrdinalSuffix(d)}
                  </option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {dayOfWeekOptions.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDay(option.value)}
                    className={`p-2 text-xs rounded-lg border transition-all ${
                      day === option.value
                        ? 'bg-primary-100 border-primary-300 text-primary-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {option.label.slice(0, 3)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateScheduleMutation.isPending}
          >
            {updateScheduleMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-1" />
                Save Schedule
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};
